import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Facebook, Twitter, MessageCircle, Send, Mail, Link2, Eye, Copy } from "lucide-react";

const SITE_URL = "https://mabuhayparish.org";
const SHARE_TEXT =
  "Saint Francis of Assisi Parish · Brgy. Mabuhay, General Santos City — a Franciscan parish of faith, mission, and hope.";

const SESSION_KEY = "sfap_view_counted_2026";

// TikTok glyph (no lucide icon for TikTok)
function TikTokIcon({ size = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.65a8.16 8.16 0 0 0 4.77 1.52V6.73a4.85 4.85 0 0 1-1.84-.04z" />
    </svg>
  );
}
function YouTubeIcon({ size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
      <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.6 3.6 12 3.6 12 3.6s-7.6 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.8.5 9.4.5 9.4.5s7.6 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z" />
    </svg>
  );
}
function InstagramIcon({ size = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.9" fill="currentColor" />
    </svg>
  );
}

const fmtViews = (n) => new Intl.NumberFormat("en-US").format(n || 0);

export default function ShareSection() {
  const [views, setViews] = useState(null);

  useEffect(() => {
    const counted = sessionStorage.getItem(SESSION_KEY);
    const url = counted ? "/views" : "/views";
    const method = counted ? "get" : "post";
    api[method](url)
      .then((res) => {
        setViews(res.data.views);
        if (!counted) sessionStorage.setItem(SESSION_KEY, "1");
      })
      .catch(() => {});
  }, []);

  const enc = (s) => encodeURIComponent(s);
  const url = enc(SITE_URL);
  const text = enc(SHARE_TEXT);

  const shares = [
    {
      key: "facebook",
      label: "Facebook",
      icon: <Facebook size={18} />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`,
      color: "hover:bg-[#1877F2] hover:border-[#1877F2]",
    },
    {
      key: "x",
      label: "X",
      icon: <Twitter size={18} />,
      href: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      color: "hover:bg-cocoa-900 hover:border-cocoa-900",
    },
    {
      key: "messenger",
      label: "Messenger",
      icon: <MessageCircle size={18} />,
      href: `https://www.facebook.com/dialog/send?link=${url}&app_id=140586622674265&redirect_uri=${url}`,
      color: "hover:bg-[#0099FF] hover:border-[#0099FF]",
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: <MessageCircle size={18} />,
      href: `https://api.whatsapp.com/send?text=${text}%20${url}`,
      color: "hover:bg-[#25D366] hover:border-[#25D366]",
    },
    {
      key: "telegram",
      label: "Telegram",
      icon: <Send size={18} />,
      href: `https://t.me/share/url?url=${url}&text=${text}`,
      color: "hover:bg-[#0088CC] hover:border-[#0088CC]",
    },
    {
      key: "email",
      label: "Email",
      icon: <Mail size={18} />,
      href: `mailto:?subject=${enc("Saint Francis of Assisi Parish — Mabuhay")}&body=${text}%20${url}`,
      color: "hover:bg-terracotta hover:border-terracotta",
    },
  ];

  // Platforms that don't have web share APIs — copy-link instead
  const copyLinks = [
    { key: "tiktok", label: "TikTok", icon: <TikTokIcon /> },
    { key: "instagram", label: "Instagram", icon: <InstagramIcon /> },
    { key: "youtube", label: "YouTube", icon: <YouTubeIcon /> },
  ];

  const copyLink = async (platform) => {
    try {
      await navigator.clipboard.writeText(SITE_URL);
      toast.success(`Link copied — paste it in your ${platform} post or bio.`);
    } catch {
      toast.error("Could not copy link. Please copy manually: " + SITE_URL);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Saint Francis of Assisi Parish",
          text: SHARE_TEXT,
          url: SITE_URL,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink("your chosen app");
    }
  };

  return (
    <section id="share" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-cocoa-900" />
      <div className="absolute inset-0 bg-gradient-to-br from-cocoa-900 via-cocoa-800 to-cocoa-900" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_30%,rgba(212,175,55,0.16),transparent_55%)]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Visitor counter */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-gold/70" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-gold font-sans">
                Pilgrims & Visitors
              </span>
            </div>
            <h2 className="font-serif text-parchment-100 text-[clamp(2rem,4vw,3.4rem)] leading-[1.1]">
              Souls who have
              <span className="block italic text-gold/95">visited this house.</span>
            </h2>

            <div
              className="mt-10 relative border border-parchment-100/15 bg-parchment-100/[0.04] backdrop-blur-sm p-8 md:p-10 overflow-hidden"
              data-testid="visitor-counter-card"
            >
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-[radial-gradient(circle,rgba(212,175,55,0.18),transparent_70%)] pointer-events-none" />

              <div className="flex items-start gap-4 text-parchment-100/80">
                <Eye className="text-gold mt-2" size={22} strokeWidth={1.4} />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.32em] text-gold mb-3 font-sans">
                    Total Visits
                  </div>
                  <div
                    className="font-serif text-7xl md:text-8xl text-parchment-100 leading-none tracking-tight tabular-nums"
                    data-testid="visitor-counter-number"
                  >
                    {views === null ? (
                      <span className="text-parchment-200/40">·····</span>
                    ) : (
                      fmtViews(views)
                    )}
                  </div>
                  <p className="mt-5 text-sm font-sans text-parchment-200/70 italic max-w-sm">
                    Every visit a prayer. Every share a witness.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Share grid */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-gold/70" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-gold font-sans">
                Spread the Word
              </span>
            </div>
            <h3 className="font-serif text-parchment-100 text-3xl md:text-4xl leading-[1.1] mb-8">
              Share the parish with family,
              <span className="block italic text-terracotta">friends, and the faithful.</span>
            </h3>

            {/* Primary share buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {shares.map((s) => (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`share-${s.key}`}
                  className={`group flex items-center gap-3 border border-parchment-100/20 bg-parchment-100/[0.03] backdrop-blur-sm px-5 py-4 text-parchment-100 transition-colors ${s.color}`}
                >
                  <span className="text-gold group-hover:text-parchment-100 transition-colors">
                    {s.icon}
                  </span>
                  <span className="font-sans text-sm tracking-wide">{s.label}</span>
                </a>
              ))}
            </div>

            {/* Copy-link platforms */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {copyLinks.map((p) => (
                <button
                  key={p.key}
                  onClick={() => copyLink(p.label)}
                  data-testid={`share-${p.key}`}
                  className="group flex items-center gap-3 border border-dashed border-parchment-100/25 bg-transparent px-5 py-4 text-parchment-100/90 hover:text-parchment-100 hover:border-gold transition-colors"
                >
                  <span className="text-gold/80 group-hover:text-gold transition-colors">
                    {p.icon}
                  </span>
                  <span className="font-sans text-sm tracking-wide">{p.label}</span>
                  <Copy size={12} className="ml-auto opacity-40 group-hover:opacity-100" />
                </button>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-parchment-200/60">
              <button
                type="button"
                onClick={nativeShare}
                data-testid="share-native"
                className="inline-flex items-center gap-2 hover:text-gold transition-colors"
              >
                <Link2 size={14} /> Quick Share
              </button>
              <span className="text-parchment-200/30">·</span>
              <button
                type="button"
                onClick={() => copyLink("your app")}
                data-testid="share-copy"
                className="inline-flex items-center gap-2 hover:text-gold transition-colors"
              >
                <Copy size={14} /> Copy Link
              </button>
              <span className="text-parchment-200/30">·</span>
              <span className="font-mono text-parchment-200/50 normal-case tracking-normal text-xs">
                {SITE_URL.replace("https://", "")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
